import { ReactElement, Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';
import { GlobalStyle } from '../styles/global-styles';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { environment } from '../utils/constants';
import Navigation from './components/Navigation';
import SparqlQueryPage from './pages/SparqlQueryPage';
import DownloadPage from './pages/DownloadPage';
import APIReferencePage from './pages/APIReferencePage';
import APIOverviewPage from './pages/APIOverviewPage';
import DataCoveragePage from './pages/DataCoveragePage';
import CPCOntologyReferencePage from './pages/CPCOntologyReferencePage';
import CPCOntologyOverviewPage from './pages/CPCOntologyOverviewPage';
import PatentOntologyReferencePage from './pages/PatentOntologyReferencePage';
import PatentOntologyOverviewPage from './pages/PatentOntologyOverviewPage';
import InternalEldaPage from './components/InternalEldaPage';
import ApiDefPage from './pages/ApiDefPage';
import ApiDocPage from './pages/ApiDocPage/ApiDocPage';
import ApiDataPage from './pages/ApiDataPage/ApiDataPage';
import ApiIdPage from './pages/ApiIdPage/ApiIdPage';

// Lazy load pages
const AboutPage = lazy(() => import('./pages/AboutPage'));

export const App = (): ReactElement => {
    let baseUri = '/' + window.location.pathname.split('/')[1] + '/';

    const currentUrl = window.location.href;

    const allowedHosts = ['localhost', 'lod.apps-staging.epo.org'];
    const url = new URL(currentUrl);

    if (allowedHosts.some((allowedHost) => url.host.startsWith(allowedHost))) {
        baseUri = `${environment.baseUri}`;
    }

    if (currentUrl.startsWith('https://endurance') || currentUrl.startsWith('https://batavia')) {
        baseUri = baseUri + window.location.pathname.split('/')[2] + '/';
    }

    return (
        <>
            <BrowserRouter basename={baseUri}>
                <Helmet defaultTitle="Linked open EP data">
                    <meta name="description" content="Linked open EP data" />
                </Helmet>
                <GlobalStyle />

                <Navigation>
                    {/* App Root Routes */}
                    <Suspense fallback="Loading...">
                        <Routes>
                            <Route path="/" element={<Navigate to="/about" />} />

                            <Route path="/linked-data/*" element={<InternalEldaPage />} />

                            <Route path='/data/*' element={<ApiDataPage />} />
                            
                            <Route path="/doc/*" element={<ApiDocPage />} />

                            <Route path="/def/*" element={<ApiDefPage />} />

                            <Route path="/id/*" element={<ApiIdPage />} />
                            
                            <Route path="/sparql-query/*" element={<SparqlQueryPage />} />
                            
                            <Route path="/documentation/api-reference" element={<APIReferencePage />} />
                            <Route path="/documentation/api-overview" element={<APIOverviewPage />} />
                            <Route path="/documentation/data-coverage" element={<DataCoveragePage />} />
                            
                            <Route
                                path="/documentation/cpc-ontology-reference"
                                element={<CPCOntologyReferencePage />}
                            />
                            
                            <Route path="/documentation/cpc-ontology-overview" element={<CPCOntologyOverviewPage />} />
                            
                            <Route
                                path="/documentation/patent-ontology-reference"
                                element={<PatentOntologyReferencePage />}
                            />
                            
                            <Route
                                path="/documentation/patent-ontology-overview"
                                element={<PatentOntologyOverviewPage />}
                            />

                            <Route path="/download/*" element={<DownloadPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </Suspense>
                </Navigation>
            </BrowserRouter>
        </>
    );
};
